/**
 * Filtros PMO sobre linhas já carregadas (pós-JQL da visão).
 * Status = classificação executiva (Atraso ≡ Crítico), não status bruto do Jira.
 */
import { JiraProjectIssueRow, PmoCarteiraTotalsResponse } from '../../types';
import {
  getMonthYearBound,
  normalizePeriodInput,
  parsePeriodInput,
} from '../../utils/monthYearParse';
import {
  EMPTY_STRUCTURED_FILTERS,
  PMO_CARTEIRA_FIELD_DEFAULT,
  PmoCarteiraGroup,
  PmoStructuredFiltersState,
  carteiraCatalog,
} from './pmoSpecConfig';
import { normalizePeriodReportScope } from './pmoReportScope';

export type ExecutiveRiskLevel = 'critico' | 'em_risco' | 'no_prazo' | 'indefinido';

const DONE_STATUS_HINTS = ['conclu', 'done', 'resolvido', 'fechado'];
const CANCEL_STATUS_HINTS = ['cancel'];
const CRITICAL_STATUS_HINTS = ['atraso', 'cutover', 'paralis', 'crítico', 'critico', 'bloque'];
const PHASE_RED_HINTS = ['vermelh', 'red', 'critico', 'crítico', 'atras', 'bloque', 'paralis'];
const PHASE_YELLOW_HINTS = ['amarel', 'yellow', 'risco'];
const PHASE_GREEN_HINTS = ['verd', 'green', 'no prazo', 'ok'];
const RISK_DUE_SOON_DAYS = 1;

const OPERACIONAL_ROW_MATCH: Record<string, string> = {
  portal_compras: 'portal de compras',
  cadastro: 'cadastro',
  compras_e: 'compras-e',
  inovacao: 'inovação',
  zpqs: 'zpqs',
  minha_petronect: 'minha petronect',
  transpetro: 'transpetro',
  automacoes: 'automações',
  level: 'level',
};

/** Classificações por carteira operacional (planilha Beatris). Match exato. */
const OPERACIONAL_CARTEIRA_CLASSIFICATION_VALUES: Record<string, string[]> = {
  portal_compras: [
    'PORTAL DE COMPRAS',
    'CONTRATO - PORTAL DE COMPRAS',
    'MELHORIAS OPERACIONAIS - PORTAL DE COMPRAS',
  ],
  cadastro: [
    'CADASTRO',
    'CONTRATO - CADASTRO DE FORNECEDORES',
    'MELHORIAS OPERACIONAIS - PORTAL CADASTRO',
  ],
  // Compras-e: sem substring genérica COMPRAS (colide com Portal).
  compras_e: [
    'COMPRAS-E',
    'COMPRAS E',
  ],
  inovacao: [
    'INOVAÇÃO',
    'INOVAÇÃO - FASE 1 CONCEPÇÃO E VALIDAÇÃO DA IDEIA',
    'INOVAÇÃO - FASE 1 CONCEPÇÃO  E VALIDAÇÃO DA IDEIA',
    'INOVAÇÃO - FASE 2 IMPLEMENTAÇÃO E AVALIAÇÃO DO MVP0',
  ],
  zpqs: ['CONTRATO - ZPQS do DPV'],
  minha_petronect: ['MINHA PETRONECT'],
  transpetro: [
    'CONTRATO - TRANSPETRO',
    'MELHORIAS OPERACIONAIS - TRANSPETRO',
  ],
  automacoes: ['MELHORIAS OPERACIONAIS - AUTOMAÇÕES'],
  level: ['CONTRATO - LEVEL'],
};

/** Match por token na classificação (sem receita / extras PMO). */
const SEM_RECEITA_CLASSIFICACAO_MATCH: Record<string, string> = {
  melhorias: 'MELHORIAS OPERACIONAIS',
  inovacao: 'INOVAÇÃO',
  minha_petronect: 'MINHA PETRONECT',
  contrato: 'CONTRATO -',
  zpqs: 'ZPQS',
  transpetro: 'TRANSPETRO',
  level: 'CONTRATO - LEVEL',
  automacoes: 'AUTOMAÇÕES',
};

/** Classificações internas (campo Classificação do Atendimento). */
const INTERNO_CLASSIFICACAO_MATCH: Record<string, string> = {
  despesas_gtec: 'DESPESAS - GTEC',
  despesas_rh: 'DESPESAS - RH',
  despesas_gserv: 'DESPESAS - GSERV',
  despesas_dar: 'DESPESAS - DAR',
  apoio_operacao: 'APOIO A OPERAÇÃO',
  ie_seguranca_infra: 'IE - SEGURANÇA DA INFORMAÇÃO E INFRAESTRUTURA',
  ie_datahouse: 'IE - DATAHOUSE PETRONECT',
  seguranca_informacao: 'SEGURANÇA DA INFORMAÇÃO',
  eventos_agendas: 'EVENTOS E AGENDAS CORPORATIVAS',
  iniciativa_estrategica: 'INICIATIVA ESTRATÉGICA',
  outros_investimentos: 'OUTROS INVESTIMENTOS',
  poc_gserv: 'POC - Gserv',
  automacao_cotacoes: 'MELHORIAS OPERACIONAIS - AUTOMAÇÃO DE COTAÇÕES',
};

const SEM_RECEITA_CLASSIFICACAO_EXACT: Record<string, string[]> = {
  // Melhorias de Portal/Cadastro passam a ser carteiras operacionais (Portal de Compras / Portal Cadastro).
  melhorias: [
    'MELHORIAS OPERACIONAIS - TRANSPETRO',
    'MELHORIAS OPERACIONAIS - AUTOMAÇÕES',
  ],
  inovacao: [
    'INOVAÇÃO',
    'INOVAÇÃO - FASE 1 CONCEPÇÃO E VALIDAÇÃO DA IDEIA',
    'INOVAÇÃO - FASE 1 CONCEPÇÃO  E VALIDAÇÃO DA IDEIA',
    'INOVAÇÃO - FASE 2 IMPLEMENTAÇÃO E AVALIAÇÃO DO MVP0',
  ],
  zpqs: ['CONTRATO - ZPQS do DPV'],
  transpetro: [
    'MELHORIAS OPERACIONAIS - TRANSPETRO',
    'CONTRATO - TRANSPETRO',
  ],
  automacoes: ['MELHORIAS OPERACIONAIS - AUTOMAÇÕES'],
  level: ['CONTRATO - LEVEL'],
  contrato: [
    'CONTRATO - ZPQS do DPV',
    'CONTRATO - TRANSPETRO',
    'CONTRATO - LEVEL',
  ],
};

export interface PmoRowFilterConfig {
  carteiraFieldId?: string;
  carteiraFieldIds?: string[];
  visibleColumns?: string[];
  ignoredVisibleColumns?: string[];
}

interface RiskEvaluationOptions {
  visibleColumns?: string[];
  ignoredVisibleColumns?: string[];
}

const norm = (value: unknown): string => String(value ?? '').trim();
const normLower = (value: unknown): string => norm(value).toLowerCase();
const normSearch = (value: unknown): string =>
  normLower(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const isMeaningfulValue = (value: unknown): boolean => {
  if (value == null) return false;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'boolean') return true;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as Record<string, unknown>).length > 0;
  return false;
};

const parseDateFromValue = (value: unknown): Date | null => {
  const raw = norm(value);
  if (!raw) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (!dateOnly) return null;
  const parsed = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  parsed.setHours(0, 0, 0, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const resolvePlannedDeliveryDate = (row: JiraProjectIssueRow): Date | null =>
  parseDateFromValue(row.go_live_planned || row.duedate);

const resolveRealDeliveryDate = (row: JiraProjectIssueRow): Date | null =>
  parseDateFromValue(row.go_live_real);

const diffInDays = (a: Date, b: Date): number => {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((a.getTime() - b.getTime()) / msPerDay);
};

const resolvePhaseColor = (status: string): 'red' | 'yellow' | 'green' | 'unknown' => {
  const normalized = normSearch(status);
  if (!normalized) return 'unknown';
  if (PHASE_RED_HINTS.some((hint) => normalized.includes(normSearch(hint)))) return 'red';
  if (PHASE_YELLOW_HINTS.some((hint) => normalized.includes(normSearch(hint)))) return 'yellow';
  if (PHASE_GREEN_HINTS.some((hint) => normalized.includes(normSearch(hint)))) return 'green';
  if (CRITICAL_STATUS_HINTS.some((hint) => normalized.includes(normSearch(hint)))) return 'red';
  if (normalized.includes('risco')) return 'yellow';
  if (isDoneStatusLabel(normalized)) return 'green';
  return 'unknown';
};

const valueForVisibleColumn = (row: JiraProjectIssueRow, columnId: string): unknown => {
  switch (columnId) {
    case 'project':
      return `${row.key || ''}${row.summary || ''}${row.project_key || ''}${row.project_name || ''}`.trim();
    case 'issue_type':
      return row.issue_type;
    case 'go_live_planned':
      return row.go_live_planned;
    case 'go_live_real':
      return row.go_live_real;
    case 'updated':
      return row.updated;
    case 'created':
      return row.created;
    case 'duedate':
      return row.duedate;
    case 'hh_vendido_seconds':
      return row.hh_vendido_seconds;
    case 'hh_real_seconds':
      return row.hh_real_seconds;
    case 'status':
      return row.status;
    default:
      return row.extra_fields?.[columnId];
  }
};

const hasMissingRequiredVisibleField = (
  row: JiraProjectIssueRow,
  visibleColumns?: string[],
  ignoredVisibleColumns?: string[],
): boolean => {
  const visible = (visibleColumns || [])
    .map((item) => norm(item))
    .filter(Boolean)
    .filter((item, index, arr) => arr.indexOf(item) === index);
  if (!visible.length) return false;
  const ignored = new Set(
    (ignoredVisibleColumns || [])
      .map((item) => norm(item))
      .filter(Boolean),
  );
  return visible.some((columnId) => {
    if (columnId === 'go_live_real') return false;
    if (columnId.startsWith('__')) return false;
    if (ignored.has(columnId)) return false;
    return !isMeaningfulValue(valueForVisibleColumn(row, columnId));
  });
};

export const isDoneStatusLabel = (status: string): boolean => {
  const value = normLower(status);
  return DONE_STATUS_HINTS.some((hint) => value.includes(hint));
};

export const isCancelledStatusLabel = (status: string): boolean => {
  const value = normLower(status);
  return CANCEL_STATUS_HINTS.some((hint) => value.includes(hint));
};

/** Backlog / Backlog Cancelado — não conta como "Em andamento". */
export const isBacklogStatusLabel = (status: string): boolean => {
  const value = normSearch(status);
  return value === 'backlog' || value.startsWith('backlog ');
};

/**
 * Fora do prazo por datas de entrega (não concluído):
 * - entrega real depois da prevista, ou
 * - prevista já passou e ainda não há data real.
 */
export const isDeliveryOutOfSchedule = (row: JiraProjectIssueRow): boolean => {
  if (isDoneStatusLabel(row.status || '')) return false;
  const plannedDate = resolvePlannedDeliveryDate(row);
  const realDate = resolveRealDeliveryDate(row);
  if (plannedDate && realDate && realDate > plannedDate) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Boolean(plannedDate && !realDate && diffInDays(plannedDate, today) < 0);
};

/** Classificação PMO: Crítico | Em risco | No prazo (legenda do slide 7). */
export const executiveRiskLevel = (row: JiraProjectIssueRow, options?: RiskEvaluationOptions): ExecutiveRiskLevel => {
  const status = normLower(row.status);
  const phaseColor = resolvePhaseColor(status);
  const plannedDate = resolvePlannedDeliveryDate(row);
  const realDate = resolveRealDeliveryDate(row);
  const soldSeconds = Number(row.hh_vendido_seconds || 0);
  const realSeconds = Number(row.hh_real_seconds || 0);
  const missingVisible = hasMissingRequiredVisibleField(row, options?.visibleColumns, options?.ignoredVisibleColumns);
  const validHours = soldSeconds > 0 && realSeconds >= 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Crítico = somente entrega real depois da prevista.
  const criticalByDate = Boolean(plannedDate && realDate && realDate > plannedDate);
  if (criticalByDate) return 'critico';

  const riskByPhase = phaseColor === 'red' || phaseColor === 'yellow';
  const riskByMissingVisibleField = missingVisible;
  const riskByOverdueWithoutReal = Boolean(plannedDate && !realDate && diffInDays(plannedDate, today) < 0);
  let riskByDueSoonWithoutReal = false;
  if (plannedDate && !realDate) {
    const daysToPlanned = diffInDays(plannedDate, today);
    riskByDueSoonWithoutReal = daysToPlanned >= 0 && daysToPlanned <= RISK_DUE_SOON_DAYS;
  }
  const riskByHoursNearLimit = validHours && realSeconds >= (soldSeconds * 0.9) && realSeconds <= soldSeconds;
  const riskByHoursOver = validHours && realSeconds > soldSeconds;
  if (
    riskByPhase
    || riskByMissingVisibleField
    || riskByOverdueWithoutReal
    || riskByDueSoonWithoutReal
    || riskByHoursNearLimit
    || riskByHoursOver
  ) {
    return 'em_risco';
  }

  const noPrazoByPhase = phaseColor === 'green';
  const noPrazoByDate = Boolean(plannedDate && realDate && realDate <= plannedDate);
  const noPrazoByHours = validHours && realSeconds <= soldSeconds;
  const noPrazoByOpenOnTrack = Boolean(
    plannedDate
    && !realDate
    && diffInDays(plannedDate, today) > RISK_DUE_SOON_DAYS
    && validHours
    && realSeconds < (soldSeconds * 0.9),
  );
  if ((noPrazoByPhase && noPrazoByDate && noPrazoByHours) || noPrazoByOpenOnTrack) return 'no_prazo';

  if (!status && !plannedDate && !realDate && !soldSeconds && !realSeconds) return 'indefinido';
  return 'em_risco';
};

/** PPTX: filtro "Atraso" ≡ legenda "Crítico". */
export const statusFilterToRiskLevel = (statusFilter: string): ExecutiveRiskLevel | null => {
  const normalized = normLower(statusFilter);
  if (normalized === 'atraso') return 'critico';
  if (normalized === 'em_risco') return 'em_risco';
  if (normalized === 'no_prazo') return 'no_prazo';
  return null;
};

const parseIsoDate = (value: unknown): Date | null => {
  const raw = norm(value);
  if (!raw) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (!dateOnly) return null;
  const parsed = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  parsed.setHours(0, 0, 0, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const matchesPeriod = (
  row: JiraProjectIssueRow,
  periodStart: string,
  periodEnd: string,
  carteiraGroup: PmoCarteiraGroup,
): boolean => {
  if (!periodStart.trim() && !periodEnd.trim()) return true;

  const dateCandidates = new Set<number>();
  const pushCandidate = (value: unknown) => {
    const parsed = parseIsoDate(value);
    if (parsed) dateCandidates.add(parsed.getTime());
  };

  if (carteiraGroup === 'sem_receita' || carteiraGroup === 'interno') {
    // Em "Projetos sem Receita" / Interno, também considera "updated" para manter cards ativos.
    pushCandidate(row.updated);
    pushCandidate(row.duedate);
    // Mantém suporte a datas em custom fields.
    Object.values(row.extra_fields || {}).forEach((value) => pushCandidate(value));
  }
  pushCandidate(row.created);
  pushCandidate(row.go_live_planned);
  pushCandidate(row.go_live_real);

  const candidates = Array.from(dateCandidates).map((ts) => new Date(ts));
  if (!candidates.length) return false;

  const startDate = periodStart.trim() ? getMonthYearBound(periodStart, 'start') : null;
  const endDate = periodEnd.trim() ? getMonthYearBound(periodEnd, 'end') : null;
  if (endDate) endDate.setHours(23, 59, 59, 999);

  return candidates.some(
    (item) => (startDate === null || item >= startDate) && (endDate === null || item <= endDate),
  );
};

const carteiraFieldCandidates = (carteiraFieldId: string, carteiraFieldIds?: string[]): string[] =>
  [
    ...(carteiraFieldIds || []),
    carteiraFieldId,
  ]
    .map((item) => norm(item))
    .filter((item, index, arr) => Boolean(item) && arr.indexOf(item) === index);

const coerceFieldTextValues = (fieldValue: unknown): string[] => {
  if (fieldValue == null || fieldValue === '') return [];
  if (Array.isArray(fieldValue)) {
    return fieldValue.map((item) => norm(item)).filter(Boolean);
  }
  if (typeof fieldValue === 'object') {
    const asRecord = fieldValue as Record<string, unknown>;
    const nested = asRecord.value ?? asRecord.name ?? asRecord.label;
    if (nested != null && nested !== fieldValue) return coerceFieldTextValues(nested);
  }
  const text = norm(fieldValue);
  return text && text !== '[object Object]' ? [text] : [];
};

const rowCarteiraFieldValues = (row: JiraProjectIssueRow, carteiraFieldId: string, carteiraFieldIds?: string[]): string[] => {
  const extra = row.extra_fields || {};
  // Um único valor efetivo — OR entre 11344/11434 misturava classificações e vazava filtro.
  // Prioridade: cf11434 (canônico) → campo configurado → demais candidatos, só se o anterior estiver vazio.
  const orderedIds = [
    PMO_CARTEIRA_FIELD_DEFAULT,
    ...carteiraFieldCandidates(carteiraFieldId, carteiraFieldIds),
  ].filter((item, index, arr) => Boolean(item) && arr.indexOf(item) === index);

  for (const fieldId of orderedIds) {
    const values = coerceFieldTextValues(extra[fieldId]);
    if (values.length) return values;
  }
  return [];
};

const rowCarteiraFallbackText = (row: JiraProjectIssueRow): string =>
  [row.summary, row.project_name, row.project_key].map(normSearch).join(' ');

/** Tag literal exata no summary — OR adicional de Compras-E (case e hífen obrigatórios). */
const COMPRAS_E_SUMMARY_TAG = '[Compras-e]';

export const hasComprasESummaryTag = (summary?: string | null): boolean =>
  String(summary || '').includes(COMPRAS_E_SUMMARY_TAG);

const containsNormalized = (text: string, token: string): boolean => {
  const textNorm = normSearch(text);
  const tokenNorm = normSearch(token);
  if (!textNorm || !tokenNorm) return false;
  if (textNorm.includes(tokenNorm)) return true;
  // Suporta variações com/sem hífen (ex.: "compras-e" vs "compras e").
  const tokenNoHyphen = tokenNorm.replace(/-/g, ' ');
  if (tokenNoHyphen !== tokenNorm && textNorm.includes(tokenNoHyphen)) return true;
  const textNoHyphen = textNorm.replace(/-/g, ' ');
  return textNoHyphen.includes(tokenNorm.replace(/-/g, ' '));
};

const resolveOperationalCarteiraFromClassification = (fieldValue: string): string | null => {
  const normalizedValue = normSearch(fieldValue);
  for (const [candidateCarteiraId, aliases] of Object.entries(OPERACIONAL_CARTEIRA_CLASSIFICATION_VALUES)) {
    if ((aliases || []).some((alias) => normSearch(alias) === normalizedValue)) {
      return candidateCarteiraId;
    }
  }
  return null;
};

const isSemReceitaCcoProject = (row: JiraProjectIssueRow): boolean => {
  const projectKey = norm(row.project_key).toUpperCase();
  if (projectKey === 'CCO') return true;
  return containsNormalized(row.project_name, 'CCO');
};

export const matchesCarteira = (
  row: JiraProjectIssueRow,
  carteiraId: string,
  carteiraGroup: PmoCarteiraGroup,
  config?: PmoRowFilterConfig,
): boolean => {
  if (!norm(carteiraId)) return true;
  const carteiraFieldId = config?.carteiraFieldId || PMO_CARTEIRA_FIELD_DEFAULT;
  const carteiraFieldIds = config?.carteiraFieldIds || [];
  const carteiraFieldValues = rowCarteiraFieldValues(row, carteiraFieldId, carteiraFieldIds);

  if (carteiraGroup === 'sem_receita') {
    if (carteiraId === 'continuidade_operacional') {
      return isSemReceitaCcoProject(row);
    }
    const exactAliases = SEM_RECEITA_CLASSIFICACAO_EXACT[carteiraId] || [];
    if (exactAliases.length) {
      // Lista explícita: não usa contains genérico (evita Melhorias Portal cair em "Melhorias").
      return carteiraFieldValues.some((value) =>
        exactAliases.some((alias) => normSearch(value) === normSearch(alias)),
      );
    }
    const classificacaoMatch = SEM_RECEITA_CLASSIFICACAO_MATCH[carteiraId];
    if (!classificacaoMatch) return false;
    // Evita que "CONTRATO - PORTAL DE COMPRAS" caia no filtro genérico de contrato sem receita.
    if (carteiraId === 'contrato') {
      return carteiraFieldValues.some((value) => {
        const n = normSearch(value);
        if (n.includes('portal de compras') || n.includes('cadastro de fornecedores')) return false;
        return containsNormalized(value, classificacaoMatch);
      });
    }
    return carteiraFieldValues.some((value) => containsNormalized(value, classificacaoMatch));
  }

  if (carteiraGroup === 'interno') {
    const classificacaoMatch = INTERNO_CLASSIFICACAO_MATCH[carteiraId];
    if (!classificacaoMatch) return false;
    return carteiraFieldValues.some((value) =>
      normSearch(value) === normSearch(classificacaoMatch) || containsNormalized(value, classificacaoMatch),
    );
  }

  if (carteiraId === 'compras_e') {
    // Compras-E: esteira CMPE/COMPRAS-E inteira; no Portal só a tag [Compras-e]
    // (mesmo com classificação Portal) ou classificação COMPRAS-E.
    // Título "Compras-e" sem colchetes / só estar na Esteira Portal NÃO entra.
    const projectKey = norm(row.project_key).toUpperCase();
    if (projectKey === 'COMPRAS-E' || projectKey === 'CMPE' || projectKey.startsWith('CMPE-')) {
      return true;
    }
    if (hasComprasESummaryTag(row.summary)) {
      return true;
    }
    if (carteiraFieldValues.length) {
      return carteiraFieldValues.some((value) =>
        (OPERACIONAL_CARTEIRA_CLASSIFICATION_VALUES.compras_e || []).some(
          (alias) => normSearch(value) === normSearch(alias),
        ),
      );
    }
    return false;
  }

  if (carteiraFieldValues.length) {
    for (const fieldValue of carteiraFieldValues) {
      const classifiedCarteiraId = resolveOperationalCarteiraFromClassification(fieldValue);
      if (classifiedCarteiraId) return classifiedCarteiraId === carteiraId;
    }
    // Classificação preenchida (Inovação/Melhorias/etc.) NÃO usa fallback pelo nome
    // "Esteira Portal de Compras" — isso puxava demandas não relacionadas.
    return false;
  }

  const fallbackMatch = OPERACIONAL_ROW_MATCH[carteiraId];
  if (!fallbackMatch) return false;
  return containsNormalized(rowCarteiraFallbackText(row), fallbackMatch);
};

export const matchesVigencia = (row: JiraProjectIssueRow, vigencia: string): boolean => {
  const normalized = normLower(vigencia);
  if (!normalized) return true;
  const status = norm(row.status);
  if (normalized === 'em_andamento') {
    // Em andamento = saiu do backlog e não está concluída/cancelada.
    return (
      !isBacklogStatusLabel(status)
      && !isDoneStatusLabel(status)
      && !isCancelledStatusLabel(status)
    );
  }
  if (normalized === 'concluido') return isDoneStatusLabel(status);
  if (normalized === 'cancelado') return isCancelledStatusLabel(status);
  return true;
};

/** Variações reais do Jira ↔ rótulo do filtro Fase. */
const PHASE_STATUS_ALIASES: Record<string, string[]> = {
  'transicao de servico': ['transicao de servicos', 'transicao de servico'],
  'avaliacao gpro': ['avaliacao gpro'],
  'pesquisa de satisfacao': ['pesquisa de satisfacao'],
  'em homologacao': ['em homologacao'],
  'em aprovacao da demanda': ['em aprovacao da demanda'],
};

const phaseMatchesStatus = (phase: string, status: string): boolean => {
  const phaseNorm = norm(phase);
  const statusNorm = norm(status);
  if (!phaseNorm) return true;
  const phaseKey = normSearch(phaseNorm);
  const statusKey = normSearch(statusNorm);
  if (statusKey === phaseKey) return true;
  const aliases = PHASE_STATUS_ALIASES[phaseKey] || [phaseKey];
  return aliases.includes(statusKey);
};

export const matchesPhase = (
  row: JiraProjectIssueRow,
  phase: string,
  vigencia: string,
): boolean => {
  const phaseNorm = norm(phase);
  if (!phaseNorm) return true;
  const vigenciaNorm = normLower(vigencia);
  if (vigenciaNorm && vigenciaNorm !== 'em_andamento') return false;
  return phaseMatchesStatus(phaseNorm, norm(row.status));
};

export const matchesStatusFilter = (
  row: JiraProjectIssueRow,
  statusFilter: string,
  options?: RiskEvaluationOptions,
): boolean => {
  const expected = statusFilterToRiskLevel(statusFilter);
  if (!expected) return true;
  return executiveRiskLevel(row, options) === expected;
};

/** Filtro por valor exato de Classificação do Atendimento (independente de carteira agrupada). */
export const matchesClassification = (
  row: JiraProjectIssueRow,
  classificationValue: string,
  config?: PmoRowFilterConfig,
): boolean => {
  const wanted = norm(classificationValue);
  if (!wanted) return true;
  const carteiraFieldId = config?.carteiraFieldId || PMO_CARTEIRA_FIELD_DEFAULT;
  const fieldValues = rowCarteiraFieldValues(row, carteiraFieldId, config?.carteiraFieldIds);
  if (!fieldValues.length) return false;
  const wantedNorm = normSearch(wanted);
  return fieldValues.some((value) => normSearch(value) === wantedNorm);
};

export const rowMatchesPmoFilters = (
  row: JiraProjectIssueRow,
  filters: PmoStructuredFiltersState,
  carteiraGroup: PmoCarteiraGroup,
  config?: PmoRowFilterConfig,
): boolean =>
  matchesPeriod(row, filters.periodStart, filters.periodEnd, carteiraGroup)
  && matchesCarteira(row, filters.carteiraId, carteiraGroup, config)
  && matchesClassification(row, filters.classificationValue, config)
  && matchesVigencia(row, filters.vigencia)
  && matchesPhase(row, filters.phase, filters.vigencia)
  && matchesStatusFilter(row, filters.statusFilter, {
    visibleColumns: config?.visibleColumns,
    ignoredVisibleColumns: config?.ignoredVisibleColumns,
  });

export const filterPmoRows = (
  rows: JiraProjectIssueRow[],
  filters: PmoStructuredFiltersState,
  carteiraGroup: PmoCarteiraGroup,
  config?: PmoRowFilterConfig,
): JiraProjectIssueRow[] =>
  rows.filter((row) => rowMatchesPmoFilters(row, filters, carteiraGroup, config));

const aggregateHhTotals = (rows: JiraProjectIssueRow[]) => {
  let hhVendidoSeconds = 0;
  let hhRealSeconds = 0;
  rows.forEach((row) => {
    const vendido = Number(row.hh_vendido_seconds || 0);
    const real = Number(row.hh_real_seconds || 0);
    hhVendidoSeconds += Number.isFinite(vendido) ? vendido : 0;
    hhRealSeconds += Number.isFinite(real) ? real : 0;
  });
  return {
    total_issues: rows.length,
    hh_vendido_hours: Math.round((hhVendidoSeconds / 3600) * 100) / 100,
    hh_real_hours: Math.round((hhRealSeconds / 3600) * 100) / 100,
  };
};

export const computeCarteiraTotals = (
  rows: JiraProjectIssueRow[],
  filters: PmoStructuredFiltersState,
  carteiraGroup: PmoCarteiraGroup,
  config?: PmoRowFilterConfig,
): PmoCarteiraTotalsResponse => {
  const baseFilters: PmoStructuredFiltersState = {
    ...filters,
    carteiraId: '',
  };
  const baseRows = filterPmoRows(rows, baseFilters, carteiraGroup, config);
  const catalog = carteiraCatalog(carteiraGroup);

  const items = catalog.map((entry) => {
    const subset = filterPmoRows(
      rows,
      { ...baseFilters, carteiraId: entry.id },
      carteiraGroup,
      config,
    );
    const totals = aggregateHhTotals(subset);
    return {
      carteira_id: entry.id,
      carteira_label: entry.label,
      ...totals,
    };
  });

  const geralTotals = aggregateHhTotals(baseRows);
  return {
    items,
    geral: {
      carteira_id: 'geral',
      carteira_label: 'Geral',
      ...geralTotals,
    },
  };
};

/** Período incompleto no rascunho não entra no filtro aplicado (dropdowns seguem funcionando). */
export const toEffectivePmoFilters = (draft: PmoStructuredFiltersState): PmoStructuredFiltersState => {
  const effective: PmoStructuredFiltersState = {
    ...draft,
    periodStart: parsePeriodInput(draft.periodStart) ? normalizePeriodInput(draft.periodStart) : '',
    periodEnd: parsePeriodInput(draft.periodEnd) ? normalizePeriodInput(draft.periodEnd) : '',
  };
  if (normLower(effective.vigencia) !== 'em_andamento') {
    effective.phase = '';
  }
  effective.reportScope = normalizePeriodReportScope(draft.reportScope);
  effective.reportMode = effective.reportScope !== 'carteira';
  return effective;
};

export const hasPartialPeriodInput = (draft: PmoStructuredFiltersState): boolean =>
  Boolean(
    (draft.periodStart.trim() && !parsePeriodInput(draft.periodStart))
    || (draft.periodEnd.trim() && !parsePeriodInput(draft.periodEnd)),
  );

export const validatePmoRowFilters = (filters: PmoStructuredFiltersState): void => {
  if (filters.periodStart && !parsePeriodInput(filters.periodStart)) {
    throw new Error('Período inicial inválido. Use dd/mm/aaaa ou mm/aaaa.');
  }
  if (filters.periodEnd && !parsePeriodInput(filters.periodEnd)) {
    throw new Error('Período final inválido. Use dd/mm/aaaa ou mm/aaaa.');
  }
  const start = filters.periodStart ? getMonthYearBound(filters.periodStart, 'start') : null;
  const end = filters.periodEnd ? getMonthYearBound(filters.periodEnd, 'end') : null;
  if (start && end && start > end) {
    throw new Error('Período inválido: data inicial maior que a final.');
  }
  if (norm(filters.phase) && normLower(filters.vigencia) !== '' && normLower(filters.vigencia) !== 'em_andamento') {
    throw new Error('Fase só pode ser filtrada quando vigência = Em andamento.');
  }
};

export const hasActivePmoRowFilters = (filters: PmoStructuredFiltersState): boolean =>
  Boolean(
    filters.periodStart.trim()
    || filters.periodEnd.trim()
    || filters.carteiraId.trim()
    || filters.classificationValue.trim()
    || filters.statusFilter.trim()
    || filters.vigencia.trim()
    || filters.phase.trim(),
  );

export const sanitizeStructuredFilters = (value: unknown): PmoStructuredFiltersState => {
  if (!value || typeof value !== 'object') {
    return { ...EMPTY_STRUCTURED_FILTERS };
  }
  const raw = value as Partial<PmoStructuredFiltersState>;
  return toEffectivePmoFilters({
    periodStart: typeof raw.periodStart === 'string' ? raw.periodStart : '',
    periodEnd: typeof raw.periodEnd === 'string' ? raw.periodEnd : '',
    carteiraId: typeof raw.carteiraId === 'string' ? raw.carteiraId : '',
    classificationValue: typeof raw.classificationValue === 'string' ? raw.classificationValue : '',
    statusFilter: typeof raw.statusFilter === 'string' ? raw.statusFilter : '',
    vigencia: typeof raw.vigencia === 'string' ? raw.vigencia : '',
    phase: typeof raw.phase === 'string' ? raw.phase : '',
    reportMode: Boolean(raw.reportMode),
    reportScope: normalizePeriodReportScope(
      typeof raw.reportScope === 'string' ? raw.reportScope : undefined,
    ),
  });
};
